// App.jsx
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './styles/App.css';

// Layout
import Layout from './components/Layout';

// Pages - Developer
import DeveloperLogin from './pages/developer/Login';
import DeveloperDashboard from './pages/developer/Dashboard';

// Pages - Admin
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';

// Pages - Shared
import ApiKeys from './pages/ApiKeys';
import ApiDocs from './pages/ApiDocs';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import TwoFactorAuth from './pages/profile/2factor';
import UpdateProfile from './pages/profile/Update';

// Context
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Loading component
const Loading = () => (
  <div className="loading-container">
    <div className="spinner"></div>
    <p>Loading...</p>
  </div>
);

// Protected Route
function ProtectedRoute({ children, adminOnly = false, developerOnly = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) return <Loading />;
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Admin-only routes
  if (adminOnly && user?.role !== 'admin' && user?.role !== 'superadmin') {
    return <Navigate to="/developer/dashboard" replace />;
  }
  
  // Developer-only routes
  if (developerOnly && user?.role === 'admin' || user?.role === 'superadmin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<DeveloperLogin />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Developer Routes */}
      <Route path="/developer" element={
        <ProtectedRoute developerOnly>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/developer/dashboard" replace />} />
        <Route path="dashboard" element={<DeveloperDashboard />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="api-docs" element={<ApiDocs />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile/2fa" element={<TwoFactorAuth />} />
        <Route path="profile/update" element={<UpdateProfile />} />
      </Route>
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminDashboard />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="api-docs" element={<ApiDocs />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile/2fa" element={<TwoFactorAuth />} />
        <Route path="profile/update" element={<UpdateProfile />} />
      </Route>
      
      {/* Default Redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
              },
            }}
          />
          <Suspense fallback={<Loading />}>
            <AppRoutes />
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;